import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../Components/Spinner";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { FaNodeJs, FaPython } from "react-icons/fa";
import MandatorySign from "../Components/MandatorySign";
import ErrorMessage from "../Components/ErrorMessage";
import getDefaultFiles from "./McpFiles";
import { categoryOptions, toolsOptions, regionOptions, runtimeOptions } from "../MCPs/ContstantValues"
import { PiLeaf } from "react-icons/pi";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const OptionButton = ({ selected, onClick, title, description, icon }) => (
    <div
        className={`mcp_type_btn fade-in ${selected ? "mcp_type_btn_selected" : ""}`}
        onClick={onClick}
    >
        <div className="mcp_type_btn_inner">
            <i>{selected ? <MdOutlineCheckCircle /> : <MdOutlineCircle />}</i>
            <span className="mcp_type_btn_title">{icon} {title}</span>
            <span className="mcp_type_btn_desc">{description}</span>
        </div>
    </div>
);

const McpBuilderTypeModal = ({
    account_id,
    show,
    handleClose
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate();
    const { org_id } = useParams()
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [type, setType] = useState("build");
    const [runtime, setRuntime] = useState("");
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [endpointUrl, setEndpointUrl] = useState(null)
    const [formData, setFormData] = useState({
        mcp_name: "",
        description: "",
        source_url: "",
        organization_ids: [`test-${org_id}`],
        category: [],
        transport_type: "HTTP",
        tools: ["AI"],
        license: "Proprietary",
        drafted_version: 0,
        region: regionOptions.find(r => r.value === "europe-west8"),
    });

    const handleSave = async () => {
        setErrorMessage({ message: "", display: false });

        if (!formData.mcp_name) {
            setErrorMessage({ message: t.abilities.ability_name_req, display: true });
            return;
        }

        if (!formData.description) {
            setErrorMessage({ message: t.abilities.ability_desc_req, display: true });
            return;
        }

        if (formData.description.length > 300) {
            setErrorMessage({ message: t.abilities.ab_desc_long, display: true });
            return;
        }

        if (formData.category.length == 0) {
            setErrorMessage({ message: t.abilities.one_cat_req, display: true });
            return;
        }

        if (!formData.transport_type) {
            setErrorMessage({ message: t.abilities.transp_req, display: true });
            return;
        }

        if (formData.tools.length == 0) {
            setErrorMessage({ message: t.abilities.tool_req, display: true });
            return;
        }

        if (!formData.license) {
            setErrorMessage({ message: t.abilities.license_req, display: true });
            return;
        }


        if (type == "build") {

            if (!formData.region) {
                setErrorMessage({ message: t.abilities.region_req, display: true });
                return;
            }
        } else {
            if (!endpointUrl) {
                setErrorMessage({ message: t.abilities.endpoint_url_req, display: true });
                return;
            }
        }


        const initialFiles = getDefaultFiles(runtime);
        const filesArray = Object.keys(initialFiles).map((filename) => ({
            filename,
            content: initialFiles[filename]
        }));

        setLoading(true);
        try {
            let ips = ""
            formData.network_mode == "restricted" ? ips = formData.allowed_ips : ips = "0.0.0.0/0"

            const response = await fetch(`${GEN_BACKEND_LINK}/save_mcp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id: null,
                    mcp_name: formData.mcp_name,
                    description: formData.description,
                    source_url: formData.source_url,
                    organization_ids: formData.organization_ids,
                    category: formData.category,
                    transport_type: formData.transport_type,
                    tools: formData.tools,
                    license: formData.license,
                    files: filesArray,
                    runtime: runtime,
                    drafted_version: formData.drafted_version,
                    region: formData.region.value,
                    endpoint_url: endpointUrl
                }),
            });

            const result = await response.json();

            if (!response.ok) throw new Error("Failed to deploy Ability");

            if (result.status == "success") {
                const mainUrl = `/e8console/organizations/${org_id}/abilities`;
                const targetPath =
                    runtime === "python"
                        ? `${mainUrl}/ability-builder/python/${result.mcp_id}/1`
                        : `${mainUrl}/ability-builder/javascript/${result.mcp_id}/1`;

                navigate(targetPath);
                CloseModal()
            }
        } catch (err) {
            setErrorMessage({ message: err.message, display: true });
        } finally {
            setLoading(false);
            setErrorMessage({ message: "", display: false });
        }
    };

    const handleNext = () => {
        setErrorMessage({ message: "", display: false })
        if (step === 1){
            if (!runtime){
                setErrorMessage({ message: t.abilities.select_option, display: true })
                return
            }
        }

        if (step === 2) {
            handleSave();
        } else if (step < 2) {
            setLoading(true)
            setTimeout(() => {
                setStep(step + 1);
                setLoading(false)
            }, 1000);
        }
    };

    const handleBack = () => {
        if (step == 2) {
            setStep(1)
            setRuntime("")
        }

        if (step == 3) {
            setStep(2)
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const CloseModal = () => {
        setFormData({
            mcp_name: "",
            description: "",
            source_url: "",
            organization_ids: [`test-${org_id}`],
            category: [],
            transport_type: "HTTP",
            tools: ["AI"],
            license: "Proprietary",
            region: regionOptions.find(r => r.value === "europe-west8"),
        })
        setErrorMessage({ message: "", display: false })
        handleClose();
        setStep(1);
        setType("build");
        setRuntime("");
        setLoading(false);
    }

    return (
        <Modal show={show} onHide={CloseModal} centered className="mcp_modal_custom">
            <div className="modal_header">
                <p className="modal_header_title">
                    {t.abilities.build_new_ability}
                    <span>{step}/2</span>
                </p>
            </div>

            {/* {step === 1 && (
                <div className="mcp_type_cont">
                    <OptionButton
                        selected={type === "build"}
                        onClick={() => setType("build")}
                        title="Build new plugin"
                        description="Create and deploy a custom plugin."
                    />
                    <OptionButton
                        selected={type === "import"}
                        onClick={() => setType("import")}
                        title="Connect external plugin"
                        description="Connect an existing plugin using its endpoint and configuration."
                    />
                </div>
            )} */}

            {step === 1 && (
                <div className="mcp_type_cont">
                    {type == "build" ? (
                        <>
                            <OptionButton
                                selected={runtime === "python"}
                                onClick={() => setRuntime("python")}
                                title="Python"
                                description={t.abilities.build_py}
                                icon={<FaPython />}
                            />
                            <OptionButton
                                selected={runtime === "javascript"}
                                onClick={() => setRuntime("javascript")}
                                title="Node.js"
                                description={t.abilities.build_js}
                                icon={<FaNodeJs />}
                            />
                        </>
                    ) : (
                        <div className="label_input">

                            <label className="input_label">{t.abilities.Runtime} <MandatorySign message={t.mandatory_field} /></label>
                            <Dropdown drop="up" autoClose={"outside"}>
                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                    {runtime
                                        ? runtimeOptions.find((r) => r.value === runtime)?.label
                                        : t.select}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="custom_dropdown_menu">
                                    <div className="scrollable_dropdown_cont">
                                        {runtimeOptions.map((item) => (
                                            <div
                                                key={item.value}
                                                onClick={() => setRuntime(item.value)}
                                                className={`settings_link ${item.value == runtime ? "settings_link_selected" : ""}`}

                                            >
                                                {item.label}
                                            </div>
                                        ))}
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    )}
                </div>
            )}

            {step === 2 && (
                <div className="modal_main_cont">

                    <div className="modal_profile_input_cont">
                        <div>
                            <div className="label_input">
                                <label className="input_label">{t.title} <MandatorySign message={t.mandatory_field} /></label>
                                <input
                                    type="text"
                                    name="mcp_name"
                                    value={formData.mcp_name}
                                    onChange={handleChange}
                                    placeholder="Ex. Customer Support Bot"
                                    className="register_form_first fade-in"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="label_input">
                                <label className="input_label">{t.description} <MandatorySign message={t.mandatory_field} /></label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder={t.abilities.ab_placeholder}
                                    className="register_form_first textarea_reg fade-in"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="label_input">
                                <label className="input_label">{t.abilities.source_url}</label>
                                <input
                                    type="text"
                                    name="source_url"
                                    value={formData.source_url}
                                    onChange={handleChange}
                                    placeholder="https://githubproject.com"
                                    className="register_form_first fade-in"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="persona_reg">
                                <div className="label_input">
                                    <label className="input_label">{t.abilities.category} <MandatorySign message={t.mandatory_field} /></label>
                                    <Dropdown drop="up" autoClose={"outside"}>
                                        <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                            {formData.category.length > 0
                                                ? `${formData.category.length} ${t.selected}`
                                                : t.select}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="custom_dropdown_menu">
                                            <div className="scrollable_dropdown_cont">

                                                {categoryOptions.map((cat) => {
                                                    const isSelected = formData.category.includes(cat);

                                                    return (
                                                        <Dropdown.Item
                                                            key={cat}
                                                            onClick={() => {
                                                                setFormData(prev => {
                                                                    const alreadySelected = prev.category.includes(cat);

                                                                    return {
                                                                        ...prev,
                                                                        category: alreadySelected
                                                                            ? prev.category.filter(c => c !== cat)
                                                                            : [...prev.category, cat]
                                                                    };
                                                                });
                                                            }}
                                                            className="settings_link"
                                                        >
                                                            <i>
                                                                {isSelected
                                                                    ? <MdOutlineCheckCircle />
                                                                    : <MdOutlineCircle />}
                                                            </i>
                                                            {cat}
                                                        </Dropdown.Item>
                                                    );
                                                })}
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                                {/* <div className="label_input">
                                    <label className="input_label">Tools <MandatorySign message={"Tools are required"} /></label>
                                    <Dropdown drop="up" autoClose={"outside"}>
                                        <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                            {formData.tools.length > 0
                                                ? `${formData.tools.length} tools selected`
                                                : "Select Tools"}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="custom_dropdown_menu">
                                            <div className="scrollable_dropdown_cont">

                                                {Object.entries(toolsOptions).map(([group, items]) => (
                                                    <div key={group}>
                                                        <div className="dropdown_group_label">{group}</div>
                                                        {items.map((tool) => (
                                                            <Dropdown.Item
                                                                key={tool}
                                                                onClick={() => {
                                                                    setFormData(prev => {
                                                                        const alreadySelected = prev.tools.includes(tool);

                                                                        return {
                                                                            ...prev,
                                                                            tools: alreadySelected
                                                                                ? prev.tools.filter(t => t !== tool)
                                                                                : [...prev.tools, tool]
                                                                        };
                                                                    });
                                                                }}
                                                                className="settings_link"
                                                            >
                                                                <i>
                                                                    {formData.tools.includes(tool)
                                                                        ? <MdOutlineCheckCircle />
                                                                        : <MdOutlineCircle />}
                                                                </i>
                                                                {tool}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div> */}
                                {type == "build" ? (
                                    <div className="label_input">
                                        <label className="input_label">{t.abilities.region} <MandatorySign message={t.mandatory_field} /></label>
                                        <Dropdown drop="up">
                                            <Dropdown.Toggle as="div" className="register_form_first fade-in">
                                                {formData.region.label}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="custom_dropdown_menu">
                                                <div className="scrollable_dropdown_cont">
                                                    {regionOptions.map((reg) => (
                                                        <Dropdown.Item
                                                            key={reg.value}
                                                            onClick={() => setFormData({ ...formData, region: reg })}
                                                            className={`settings_link ${reg.value == formData.region.value ? "settings_link_selected" : ""}`}
                                                        >
                                                            {reg.label}
                                                            <span>{reg.lowCo2 && <i><PiLeaf /></i>} {reg.value} </span>
                                                        </Dropdown.Item>
                                                    ))}
                                                </div>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                ) : (
                                    <div className="label_input">
                                        <label className="input_label">{t.abilities.endpoint_url}<MandatorySign message={t.mandatory_field} /></label>
                                        <input
                                            type="text"
                                            name="source_url"
                                            value={endpointUrl}
                                            onChange={(e) => setEndpointUrl(e.target.value)}
                                            placeholder="https://my-endpoint-url.com"
                                            className="register_form_first fade-in"
                                            autoComplete="off"
                                        />
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>
                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleNext}
                        disabled={
                            loading ||
                            (step === 1 && !type) ||
                            (step === 2 && !runtime)
                        }
                    >
                        {loading ? <Spinner loading={true} /> : step === 3 ? t.start : t.next}
                    </button>

                    {step > 1 && (
                        <button className="modale_close_btn" onClick={handleBack}>
                            <i className="fa-solid fa-chevron-left"></i> {t.back}
                        </button>
                    )}

                    {step === 1 && (
                        <button className="modale_close_btn" onClick={CloseModal}>
                            {t.close}
                        </button>
                    )}
                </div>
            </div>
        </Modal >
    );
};

export default McpBuilderTypeModal;